import React, { useState, useEffect, useMemo, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { parseISO, format, isValid } from "date-fns";
import {
  CTable,
  CContainer,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CCol,
  CFormLabel,
  CFormSelect,
  CButton,
  CSpinner,
  CAlert,
  CBadge,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CFormCheck,
  CRow,
  CCard,
  CCardBody,
} from "@coreui/react";
import settingsStore from "../../store/SettingsStore";
import cashShiftsStore from "../../store/CashShiftsStore";
import PeriodFilters from "../UI/PeriodFilters/PeriodFilters";
import ServerSelect from "../UI/PeriodFilters/ServerSelect";
import { fmtInt } from "../../utils/chartHelpers";

const STATUS_LABELS = {
  OPEN: "Открыта",
  CLOSED: "Закрыта",
  ACCEPTED: "Принята",
  UNACCEPTED: "Не принята",
  HASWARNINGS: "Подозрительная",
  ANY: "Любая",
};

const SHIFT_FIELD_LABELS = {
  id: "ID смены",
  sessionNumber: "Номер кассовой смены",
  fiscalNumber: "Фискальный номер смены",
  cashRegNumber: "Номер ФР",
  cashRegSerial: "Серийный номер ФР",
  openDate: "Дата открытия смены",
  closeDate: "Дата закрытия смены",
  acceptDate: "Дата принятия смены",
  sessionStatus: "Статус смены",
  sessionStaus: "Статус смены",
  managerId: "Ответственный менеджер",
  managerName: "Ответственный менеджер",
  manager: "Ответственный менеджер",
  responsibleUser: "Ответственный кассир",
  responsibleUserName: "Ответственный кассир",
  sessionStartCash: "Остаток в кассе на начало дня",
  payOrders: "Сумма всех заказов с учётом скидки",
  sumWriteoffOrders: "Сумма заказов, закрытых за счёт заведения",
  salesCash: "Сумма продаж за наличные",
  salesCredit: "Сумма продаж в кредит",
  salesCerdit: "Сумма продаж в кредит",
  salesCard: "Сумма продаж по картам",
  conceptionId: "Концепция",
  payIn: "Сумма всех внесений",
  payOut: "Сумма всех изъятий",
  payIncome: "Сумма изъятия в конце смены",
  cashRemain: "Остаток в кассе после закрытия смены",
  cashDiff: "Расхождение книжных и фактических сумм",
  conception: "Концепция",
  pointOfSale: "Точка продаж",
  departmentId: "Подразделение",
  pointOfSaleId: "Точка продаж (ID)",
  groupId: "Группа",
};

const MONEY_KEYS = new Set([
  "sessionStartCash",
  "payOrders",
  "sumWriteoffOrders",
  "salesCash",
  "salesCredit",
  "salesCerdit",
  "salesCard",
  "payIn",
  "payOut",
  "payIncome",
  "cashRemain",
  "cashDiff",
]);

const DATE_KEYS = new Set(["openDate", "closeDate", "acceptDate"]);

const DETAIL_SECTIONS = [
  {
    title: "Смена и регистратор",
    keys: ["sessionNumber", "fiscalNumber", "cashRegNumber", "cashRegSerial"],
  },
  {
    title: "Даты и статус",
    keys: ["openDate", "closeDate", "acceptDate", "sessionStatus", "sessionStaus"],
  },
  {
    title: "Ответственные",
    keys: ["managerId", "responsibleUser"],
  },
  {
    title: "Обороты и остатки",
    keys: [
      "sessionStartCash",
      "payOrders",
      "sumWriteoffOrders",
      "salesCash",
      "salesCredit",
      "salesCerdit",
      "salesCard",
      "payIn",
      "payOut",
      "payIncome",
      "cashRemain",
      "cashDiff",
    ],
  },
  {
    title: "Точка и концепция",
    keys: ["conception", "conceptionId", "pointOfSale", "pointOfSaleId", "departmentId", "groupId"],
  },
  {
    title: "Служебное",
    keys: ["id"],
  },
];

const DETAIL_SKIP_KEYS = new Set([
  "managername",
  "manager",
  "responsibleusername",
  "responsibleuserid",
]);

const ALL_DETAIL_KEYS = new Set(DETAIL_SECTIONS.flatMap((s) => s.keys.map((k) => k.toLowerCase())));

const TABLE_COL_COUNT = 11;

function pad2(n) {
  return String(n).padStart(2, "0");
}

function getShiftStatus(shift) {
  return shift?.sessionStatus ?? shift?.sessionStaus;
}

function formatDateTime(v) {
  if (!v) return "—";
  const raw = String(v).trim();
  let d = parseISO(raw);
  if (!isValid(d)) d = parseISO(raw.replace(" ", "T"));
  if (!isValid(d)) {
    const fallback = new Date(raw);
    if (isValid(fallback)) d = fallback;
  }
  if (!isValid(d)) return raw;
  return format(d, "dd.MM.yyyy HH:mm:ss");
}

function formatMoney(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString("ru-RU", { maximumFractionDigits: 2 });
}

function statusBadgeColor(status) {
  switch (status) {
    case "ACCEPTED":
      return "success";
    case "OPEN":
      return "info";
    case "HASWARNINGS":
    case "UNACCEPTED":
      return "warning";
    case "CLOSED":
      return "secondary";
    default:
      return "dark";
  }
}

function personLabel(name, id) {
  if (name) return name;
  if (id) return id;
  return "—";
}

function formatEntityLabel(v) {
  if (v == null || v === "") return null;
  if (typeof v === "string") return v;
  if (typeof v === "object") {
    return v.name ?? v.title ?? v.displayName ?? null;
  }
  return String(v);
}

function cashRegLabel(shift) {
  const num = shift.cashRegNumber != null ? `№${shift.cashRegNumber}` : null;
  const fiscal = shift.fiscalNumber != null ? `ф.${shift.fiscalNumber}` : null;
  if (num && fiscal) return `${num} / ${fiscal}`;
  return num || fiscal || "—";
}

function findShiftKey(shift, fieldKey) {
  if (!shift) return null;
  if (Object.prototype.hasOwnProperty.call(shift, fieldKey)) return fieldKey;
  const lower = fieldKey.toLowerCase();
  return Object.keys(shift).find((k) => k.toLowerCase() === lower) ?? null;
}

function getShiftValue(shift, fieldKey) {
  const actual = findShiftKey(shift, fieldKey);
  return actual != null ? shift[actual] : undefined;
}

function normalizeFieldKey(key) {
  const lower = key.toLowerCase();
  if (lower === "conceptionid") return "conceptionId";
  if (lower === "salescredit") return "salesCredit";
  if (lower === "salescerdit") return "salesCerdit";
  if (lower === "sessionstaus") return "sessionStaus";
  return key;
}

function fieldLabel(key) {
  const norm = normalizeFieldKey(key);
  return SHIFT_FIELD_LABELS[norm] || SHIFT_FIELD_LABELS[key] || key;
}

function getDetailSkipKeys(shift) {
  const skip = new Set(DETAIL_SKIP_KEYS);
  if (getShiftValue(shift, "sessionStatus") != null && getShiftValue(shift, "sessionStatus") !== "") {
    skip.add("sessionstaus");
  }
  const conception = getShiftValue(shift, "conception");
  if (conception != null && conception !== "") {
    skip.add("conceptionid");
  }
  const salesCredit = getShiftValue(shift, "salesCredit");
  if (salesCredit != null && salesCredit !== "") {
    skip.add("salescerdit");
  }
  return skip;
}

function shouldSkipDetailKey(shift, key, skip) {
  const lower = key.toLowerCase();
  if (skip.has(lower)) return true;
  return false;
}

function formatShiftFieldValue(fieldKey, value, shift) {
  const key = normalizeFieldKey(fieldKey);
  if (key === "acceptDate") {
    if (value == null || value === "") return "Смена не принята";
    return formatDateTime(value);
  }
  if (key === "managerId") {
    return personLabel(
      shift.managerName ?? formatEntityLabel(shift.manager),
      shift.managerId ?? shift.manager,
    );
  }
  if (key === "responsibleUser") {
    return personLabel(
      shift.responsibleUserName ?? formatEntityLabel(value),
      typeof value === "string" ? value : null,
    );
  }
  if (key === "conception" || key === "conceptionId") {
    return formatEntityLabel(getShiftValue(shift, "conception")) || formatEntityLabel(value) || "—";
  }
  if (key === "pointOfSale") {
    return formatEntityLabel(value) || "—";
  }
  if (value == null || value === "") return "—";
  if (DATE_KEYS.has(key)) return formatDateTime(value);
  if (MONEY_KEYS.has(key)) return `${formatMoney(value)} ₽`;
  if (key === "sessionStatus" || key === "sessionStaus") {
    return STATUS_LABELS[value] || String(value);
  }
  return String(value);
}

function makeDetailEntry(shift, fieldKey) {
  const actualKey = findShiftKey(shift, fieldKey);
  if (!actualKey) return null;
  return {
    key: actualKey,
    label: fieldLabel(fieldKey),
    value: formatShiftFieldValue(fieldKey, shift[actualKey], shift),
  };
}

function buildShiftDetailSections(shift) {
  if (!shift) return [];
  const skip = getDetailSkipKeys(shift);
  const usedKeys = new Set();

  const sections = DETAIL_SECTIONS.map((section) => {
    const entries = section.keys
      .map((fieldKey) => {
        const actual = findShiftKey(shift, fieldKey);
        if (!actual || shouldSkipDetailKey(shift, actual, skip)) return null;
        usedKeys.add(actual.toLowerCase());
        return makeDetailEntry(shift, fieldKey);
      })
      .filter(Boolean);
    return entries.length ? { title: section.title, entries } : null;
  }).filter(Boolean);

  const extra = Object.keys(shift)
    .filter((k) => !usedKeys.has(k.toLowerCase()) && !shouldSkipDetailKey(shift, k, skip))
    .filter((k) => !ALL_DETAIL_KEYS.has(k.toLowerCase()))
    .sort((a, b) => fieldLabel(a).localeCompare(fieldLabel(b), "ru"))
    .map((k) => makeDetailEntry(shift, k))
    .filter(Boolean);

  if (extra.length) {
    sections.push({ title: "Дополнительно", entries: extra });
  }

  return sections;
}

function mapShiftToRow(shift) {
  const st = getShiftStatus(shift);
  return {
    key: shift.id ?? `${shift.sessionNumber}-${shift.openDate}`,
    shift,
    sessionNumber: shift.sessionNumber ?? "—",
    cashReg: cashRegLabel(shift),
    openLabel: formatDateTime(shift.openDate),
    closeLabel: formatDateTime(shift.closeDate),
    status: st,
    statusLabel: STATUS_LABELS[st] || st || "—",
    cashier: personLabel(shift.managerName, shift.managerId ?? shift.manager),
    payOrders: `${formatMoney(shift.payOrders)} ₽`,
    salesCash: `${formatMoney(shift.salesCash)} ₽`,
    salesCard: `${formatMoney(shift.salesCard)} ₽`,
    cashDiff: `${formatMoney(shift.cashDiff)} ₽`,
  };
}

function CashShiftsMobileList({ rows, loading, onDetail }) {
  if (rows.length === 0 && !loading) {
    return <p className="text-center text-body-secondary py-4 mb-0">Нет данных. Нажмите «Загрузить».</p>;
  }
  return (
    <div className="cash-shifts-mobile dash-mobile-cards">
      {rows.map((row) => (
        <article key={row.key} className="dangerous-ops-card">
          <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
            <div>
              <div className="dangerous-ops-card-type">Смена №{row.sessionNumber}</div>
              <div className="dangerous-ops-card-date">{row.openLabel}</div>
            </div>
            <CBadge color={statusBadgeColor(row.status)}>{row.statusLabel}</CBadge>
          </div>
          <div className="dangerous-ops-field">
            <div className="dangerous-ops-field-label">ФР</div>
            <div className="dangerous-ops-field-value">{row.cashReg}</div>
          </div>
          <div className="dangerous-ops-field">
            <div className="dangerous-ops-field-label">Закрыта</div>
            <div className="dangerous-ops-field-value">{row.closeLabel}</div>
          </div>
          <div className="dangerous-ops-field">
            <div className="dangerous-ops-field-label">Кассир</div>
            <div className="dangerous-ops-field-value">{row.cashier}</div>
          </div>
          <div className="dangerous-ops-field">
            <div className="dangerous-ops-field-label">Заказы</div>
            <div className="dangerous-ops-field-value">{row.payOrders}</div>
          </div>
          <div className="dangerous-ops-field">
            <div className="dangerous-ops-field-label">Наличные / Карта</div>
            <div className="dangerous-ops-field-value">
              {row.salesCash} / {row.salesCard}
            </div>
          </div>
          <div className="dangerous-ops-field">
            <div className="dangerous-ops-field-label">Расхождение</div>
            <div className="dangerous-ops-field-value">{row.cashDiff}</div>
          </div>
          <CButton color="primary" variant="outline" size="sm" className="mt-3 w-100" onClick={() => onDetail(row.shift)}>
            Подробнее
          </CButton>
        </article>
      ))}
    </div>
  );
}

function CashShiftDetailModal({ shift, onClose }) {
  const sections = useMemo(() => buildShiftDetailSections(shift), [shift]);
  const title = shift?.sessionNumber != null ? `Смена №${shift.sessionNumber}` : "Детализация смены";

  return (
    <CModal
      visible={!!shift}
      onClose={onClose}
      size="lg"
      scrollable
      alignment="center"
      className="cash-shift-detail-modal"
    >
      <CModalHeader>
        <CModalTitle>{title}</CModalTitle>
      </CModalHeader>
      <CModalBody className="cash-shift-detail">
        {sections.map((section) => (
          <section key={section.title} className="cash-shift-detail-section">
            <h6 className="cash-shift-detail-section-title">{section.title}</h6>
            {section.entries.map((item) => (
              <div key={item.key} className="cash-shift-detail-row">
                <div className="cash-shift-detail-field-label">{item.label}</div>
                <div className="cash-shift-detail-field-value">{item.value}</div>
              </div>
            ))}
          </section>
        ))}
      </CModalBody>
      <CModalFooter className="cash-shift-detail-footer">
        <CButton color="secondary" className="w-100 w-sm-auto" onClick={onClose}>
          Закрыть
        </CButton>
      </CModalFooter>
    </CModal>
  );
}

const CashShiftsPage = observer(() => {
  const [selectedServer, setSelectedServer] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [status, setStatus] = useState("ANY");
  const [detailShift, setDetailShift] = useState(null);
  const [quickRange, setQuickRange] = useState("week");

  useEffect(() => {
    const init = async () => {
      const userId = localStorage.getItem("userId");
      if (userId) await settingsStore.getServers(userId);
      const today = new Date();
      const to = `${today.getFullYear()}-${pad2(today.getMonth() + 1)}-${pad2(today.getDate())}`;
      const past = new Date(today);
      past.setDate(past.getDate() - 7);
      const from = `${past.getFullYear()}-${pad2(past.getMonth() + 1)}-${pad2(past.getDate())}`;
      setDateTo(to);
      setDateFrom(from);
    };
    init();
  }, []);

  useEffect(() => {
    if (!settingsStore.servers?.length) return;
    if (selectedServer === "") {
      setSelectedServer(String(settingsStore.servers[0].id));
    }
  }, [settingsStore.servers, selectedServer]);

  const loadShifts = useCallback(() => {
    if (!selectedServer || !dateFrom || !dateTo) return;
    cashShiftsStore.fetchShifts(selectedServer, dateFrom, dateTo, {
      status,
      employeesIncludeDeleted: true,
    });
  }, [selectedServer, dateFrom, dateTo, status]);

  useEffect(() => {
    loadShifts();
  }, [loadShifts]);

  const rows = useMemo(
    () => (cashShiftsStore.filteredShifts || []).map(mapShiftToRow),
    [cashShiftsStore.filteredShifts, cashShiftsStore.filterDiffOnly, cashShiftsStore.filterWarningsOnly],
  );

  const summary = cashShiftsStore.summary;

  const rowStyle = (shift) => {
    const cd = Number(shift.cashDiff);
    const st = getShiftStatus(shift);
    if (!Number.isNaN(cd) && cd !== 0) return { backgroundColor: "rgba(220, 53, 69, 0.08)" };
    if (st === "HASWARNINGS") return { backgroundColor: "rgba(255, 193, 7, 0.12)" };
    return undefined;
  };

  return (
    <CContainer fluid className="px-3" style={{ flex: "1", marginTop: "120px", maxWidth: "100%" }}>
      <CCol className="mb-3">
        <h4 className="mb-0">Кассовые смены</h4>
      </CCol>

      <ServerSelect
        servers={settingsStore.servers}
        value={selectedServer}
        onChange={(e) => setSelectedServer(e.target.value)}
        id="srvCash"
        label="Сервер iiko"
      />

      <PeriodFilters
        fromDate={dateFrom}
        toDate={dateTo}
        onFromChange={setDateFrom}
        onToChange={setDateTo}
        onRefresh={loadShifts}
        maxDays={90}
        quickRange={quickRange}
        onQuickRangeChange={setQuickRange}
        refreshLabel={cashShiftsStore.isLoading ? "Загрузка…" : "Загрузить"}
        disabled={cashShiftsStore.isLoading}
      />

      <div className="mb-3" style={{ maxWidth: 280 }}>
        <CFormLabel htmlFor="csStatus">Статус</CFormLabel>
        <CFormSelect id="csStatus" value={status} onChange={(e) => setStatus(e.target.value)}>
          {Object.entries(STATUS_LABELS).map(([val, label]) => (
            <option key={val} value={val}>
              {label}
            </option>
          ))}
        </CFormSelect>
      </div>

      <div className="d-flex flex-wrap gap-3 mb-3">
        <CFormCheck
          id="filterDiff"
          label="Только с расхождением"
          checked={cashShiftsStore.filterDiffOnly}
          onChange={(e) => cashShiftsStore.setFilterDiffOnly(e.target.checked)}
        />
        <CFormCheck
          id="filterWarn"
          label="Только подозрительные"
          checked={cashShiftsStore.filterWarningsOnly}
          onChange={(e) => cashShiftsStore.setFilterWarningsOnly(e.target.checked)}
        />
      </div>

      <CRow className="g-3 mb-3">
        <CCol sm={6} lg={3}>
          <CCard>
            <CCardBody>
              <div className="text-body-secondary small">Смен</div>
              <div className="fs-5 fw-semibold">{summary.totalShifts}</div>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol sm={6} lg={3}>
          <CCard>
            <CCardBody>
              <div className="text-body-secondary small">Заказы</div>
              <div className="fs-5 fw-semibold">{fmtInt(summary.totalPayOrders)} ₽</div>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol sm={6} lg={3}>
          <CCard>
            <CCardBody>
              <div className="text-body-secondary small">Расхождения</div>
              <div className="fs-5 fw-semibold">{summary.diffCount}</div>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol sm={6} lg={3}>
          <CCard>
            <CCardBody>
              <div className="text-body-secondary small">Подозрительных</div>
              <div className="fs-5 fw-semibold">{summary.warningCount}</div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {cashShiftsStore.errorMessage && (
        <CAlert color="danger" className="mb-3">
          {cashShiftsStore.errorMessage}
        </CAlert>
      )}

      <p className="text-body-secondary small mb-2">
        Показано смен: {rows.length} из {cashShiftsStore.shifts.length}
      </p>

      <div className="d-lg-none">
        {cashShiftsStore.isLoading && rows.length === 0 && (
          <p className="text-center text-body-secondary py-4 mb-0">
            <CSpinner size="sm" className="me-2" />
            Загрузка…
          </p>
        )}
        <CashShiftsMobileList rows={rows} loading={cashShiftsStore.isLoading} onDetail={setDetailShift} />
      </div>

      <div className="d-none d-lg-block table-responsive">
        <CTable hover bordered className="mb-0 align-middle">
          <CTableHead>
            <CTableRow>
              <CTableHeaderCell>Смена №</CTableHeaderCell>
              <CTableHeaderCell>ФР №</CTableHeaderCell>
              <CTableHeaderCell>Открыта</CTableHeaderCell>
              <CTableHeaderCell>Закрыта</CTableHeaderCell>
              <CTableHeaderCell>Статус</CTableHeaderCell>
              <CTableHeaderCell>Кассир</CTableHeaderCell>
              <CTableHeaderCell>Заказы</CTableHeaderCell>
              <CTableHeaderCell>Наличные</CTableHeaderCell>
              <CTableHeaderCell>Карта</CTableHeaderCell>
              <CTableHeaderCell>Расхождение</CTableHeaderCell>
              <CTableHeaderCell />
            </CTableRow>
          </CTableHead>
          <CTableBody>
            {rows.length === 0 && !cashShiftsStore.isLoading && (
              <CTableRow>
                <CTableDataCell colSpan={TABLE_COL_COUNT} className="text-center text-body-secondary">
                  Нет данных. Нажмите «Загрузить».
                </CTableDataCell>
              </CTableRow>
            )}
            {rows.map((row) => (
              <CTableRow key={row.key} style={rowStyle(row.shift)}>
                <CTableDataCell>{row.sessionNumber}</CTableDataCell>
                <CTableDataCell className="text-break">{row.cashReg}</CTableDataCell>
                <CTableDataCell className="text-nowrap">{row.openLabel}</CTableDataCell>
                <CTableDataCell className="text-nowrap">{row.closeLabel}</CTableDataCell>
                <CTableDataCell>
                  <CBadge color={statusBadgeColor(row.status)}>{row.statusLabel}</CBadge>
                </CTableDataCell>
                <CTableDataCell className="text-break">{row.cashier}</CTableDataCell>
                <CTableDataCell>{row.payOrders}</CTableDataCell>
                <CTableDataCell>{row.salesCash}</CTableDataCell>
                <CTableDataCell>{row.salesCard}</CTableDataCell>
                <CTableDataCell>{row.cashDiff}</CTableDataCell>
                <CTableDataCell>
                  <CButton color="primary" variant="outline" size="sm" onClick={() => setDetailShift(row.shift)}>
                    Подробнее
                  </CButton>
                </CTableDataCell>
              </CTableRow>
            ))}
          </CTableBody>
        </CTable>
      </div>

      <CashShiftDetailModal shift={detailShift} onClose={() => setDetailShift(null)} />
    </CContainer>
  );
});

export default CashShiftsPage;
